import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  Box,
  Typography,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { useNavigate } from 'react-router-dom';
import { projects } from '../../data/projects';
import { essays, bookDetails } from '../../data/writing';
import { bookshelf } from '../../data/bookshelf';
import { profile } from '../../data/profile';
import { isSoundEnabled, setSoundEnabled, playSwitchSound, playClickSound } from '../../utils/soundEffects';

const CommandPalette = ({ open, onClose, mode, toggleTheme, onOpenTerminal, onOpenRecruiter }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copiedToast, setCopiedToast] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const listRef = useRef(null);

  // Reset query and selection on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setCopiedToast(false);
    }
  }, [open]);

  // Base Commands & Items
  const allCommands = useMemo(() => {
    const actions = [
      {
        id: 'recruiter-mode',
        title: 'Recruiter Mode (30-Sec Executive Briefing)',
        category: 'Quick Actions',
        icon: <FlashOnIcon sx={{ color: isDark ? '#fbbf24' : '#d97706' }} />,
        shortcut: 'B',
        action: () => {
          onClose();
          if (onOpenRecruiter) onOpenRecruiter();
        }
      },
      {
        id: 'codeforces-profile',
        title: 'Codeforces Telemetry & Live Profile (2013 Peak Rating)',
        category: 'Quick Actions',
        icon: <TimelineOutlinedIcon sx={{ color: '#c084fc' }} />,
        shortcut: 'C',
        action: () => {
          navigate('/codeforces');
          playClickSound();
          onClose();
        }
      },
      {
        id: 'toggle-theme',
        title: mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode',
        category: 'Quick Actions',
        icon: mode === 'dark' ? <LightModeOutlinedIcon color="secondary" /> : <DarkModeOutlinedIcon color="secondary" />,
        shortcut: 'T',
        action: () => {
          toggleTheme();
          playSwitchSound();
          onClose();
        }
      },
      {
        id: 'toggle-sound',
        title: isSoundEnabled() ? 'Mute Mechanical Audio FX' : 'Enable Mechanical Audio FX',
        category: 'Quick Actions',
        icon: isSoundEnabled() ? <VolumeOffOutlinedIcon color="secondary" /> : <VolumeUpOutlinedIcon color="secondary" />,
        shortcut: 'S',
        action: () => {
          const next = !isSoundEnabled();
          setSoundEnabled(next);
          if (next) playSwitchSound();
          onClose();
        }
      },
      {
        id: 'launch-terminal',
        title: 'Launch Unix Web Shell ( >_ )',
        category: 'Quick Actions',
        icon: <TerminalIcon color="secondary" />,
        shortcut: '~',
        action: () => {
          onClose();
          if (onOpenTerminal) onOpenTerminal();
        }
      },
      {
        id: 'copy-email',
        title: `Copy Direct Email (${profile.email})`,
        category: 'Quick Actions',
        icon: <EmailOutlinedIcon color="secondary" />,
        shortcut: 'E',
        action: () => {
          if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(profile.email);
          }
          setCopiedToast(true);
          playClickSound();
          setTimeout(() => {
            onClose();
          }, 600);
        }
      },
      {
        id: 'download-resume',
        title: 'Download Curriculum Vitae (PDF)',
        category: 'Quick Actions',
        icon: <DescriptionOutlinedIcon color="secondary" />,
        shortcut: 'R',
        action: () => {
          window.open('/resume.pdf', '_blank', 'noopener,noreferrer');
          playClickSound();
          onClose();
        }
      },
      {
        id: 'algo-simulator',
        title: 'Algorithm Systems & Case Studies',
        category: 'Quick Actions',
        icon: <CodeIcon color="secondary" />,
        action: () => {
          navigate('/work');
          playClickSound();
          onClose();
        }
      }
    ];

    const navigation = [
      { id: 'nav-home', title: 'Home // Architecture Overview', category: 'Navigation', path: '/' },
      { id: 'nav-about', title: 'About // Background & Philosophy', category: 'Navigation', path: '/about' },
      { id: 'nav-work', title: 'Selected Work & Systems Catalogue', category: 'Navigation', path: '/work' },
      { id: 'nav-codeforces', title: 'Codeforces // Candidate Master (2013)', category: 'Navigation', path: '/codeforces' },
      { id: 'nav-writing', title: 'Writing Space // Essays & Manuscript', category: 'Navigation', path: '/writing' },
      { id: 'nav-reading', title: 'Personal Library & Bookshelf', category: 'Navigation', path: '/reading' },
      { id: 'nav-learning', title: 'Knowledge Graph & Research Journey', category: 'Navigation', path: '/learning' },
      { id: 'nav-journey', title: 'Milestones & Systems Evolution', category: 'Navigation', path: '/journey' },
      { id: 'nav-resume', title: 'Resume // Curriculum Vitae', category: 'Navigation', path: '/resume' },
      { id: 'nav-contact', title: 'Contact // Direct Correspondence', category: 'Navigation', path: '/contact' },
    ].map(item => ({
      ...item,
      icon: <BookmarkBorderIcon sx={{ color: theme.palette.text.secondary }} />,
      action: () => {
        navigate(item.path);
        playClickSound();
        onClose();
      }
    }));

    const projectItems = projects.map(p => ({
      id: `proj-${p.id}`,
      title: `${p.title} (${p.category.toUpperCase()})`,
      category: 'Case Studies',
      icon: <ScienceOutlinedIcon color="secondary" />,
      action: () => {
        navigate(`/work/${p.slug}`);
        playClickSound();
        onClose();
      }
    }));

    const bookManuscriptItem = [
      {
        id: 'book-manuscript',
        title: `${bookDetails.title} (Book Manuscript)`,
        category: 'Book Manuscript',
        icon: <MenuBookOutlinedIcon sx={{ color: '#c084fc' }} />,
        action: () => {
          navigate('/writing/book');
          playClickSound();
          onClose();
        }
      }
    ];

    const essayItems = (essays || []).map(w => ({
      id: `write-${w.id}`,
      title: `${w.title} [Technical Essay]`,
      category: 'Writings & Essays',
      icon: <HistoryEduIcon sx={{ color: theme.palette.text.secondary }} />,
      action: () => {
        navigate(`/writing/${w.slug}`);
        playClickSound();
        onClose();
      }
    }));

    const bookItems = (bookshelf || []).map(b => ({
      id: `book-${b.id}`,
      title: `${b.title} by ${b.author} (${b.category})`,
      category: 'Bookshelf Catalogue',
      icon: <MenuBookOutlinedIcon sx={{ color: theme.palette.text.secondary }} />,
      action: () => {
        navigate(`/reading/${b.slug}`);
        playClickSound();
        onClose();
      }
    }));

    return [...actions, ...navigation, ...projectItems, ...bookManuscriptItem, ...essayItems, ...bookItems];
  }, [mode, isDark, toggleTheme, onOpenTerminal, onOpenRecruiter, onClose, navigate, theme.palette.text.secondary]);

  // Filter commands by query
  const filtered = useMemo(() => {
    if (!query.trim()) return allCommands;
    const q = query.toLowerCase();
    return allCommands.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.category.toLowerCase().includes(q)
    );
  }, [allCommands, query]);

  // Auto-scroll into view for keyboard navigation
  useEffect(() => {
    if (listRef.current && listRef.current.children[selectedIndex]) {
      listRef.current.children[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Keydown handler
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filtered.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filtered.length) % (filtered.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        filtered[selectedIndex].action();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: isDark ? 'rgba(10, 13, 19, 0.95)' : 'rgba(255, 255, 255, 0.96)',
          backdropFilter: 'blur(24px)',
          border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.3)' : 'rgba(2, 132, 199, 0.25)'}`,
          boxShadow: isDark
            ? '0 24px 64px rgba(0, 0, 0, 0.8), 0 0 32px rgba(56, 189, 248, 0.15)'
            : '0 24px 64px rgba(0, 0, 0, 0.12), 0 0 32px rgba(2, 132, 199, 0.1)',
          overflow: 'hidden',
          m: { xs: 2, sm: 'auto' },
        }
      }}
      onKeyDown={handleKeyDown}
    >
      {/* Search Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.8,
          borderBottom: `1px solid ${theme.palette.divider}`,
          gap: 1.5,
        }}
      >
        <SearchIcon sx={{ color: theme.palette.secondary.main, fontSize: 22 }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIndex(0);
          }}
          placeholder="Type a command, project, or jump to page..."
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.94rem',
            color: theme.palette.text.primary,
          }}
        />
        <Chip
          label="ESC"
          size="small"
          onClick={onClose}
          sx={{
            fontSize: '0.62rem',
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            borderRadius: '4px',
            height: 20,
            cursor: 'pointer',
          }}
        />
        <IconButton size="small" onClick={onClose} aria-label="Close command palette">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Result Items List */}
      <List
        ref={listRef}
        sx={{
          maxHeight: 380,
          overflowY: 'auto',
          p: 1,
        }}
      >
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontFamily: '"JetBrains Mono", monospace' }}>
              No matching commands found for "{query}"
            </Typography>
          </Box>
        ) : (
          filtered.map((item, idx) => {
            const isSelected = selectedIndex === idx;
            return (
              <ListItemButton
                key={item.id}
                onClick={item.action}
                selected={isSelected}
                sx={{
                  borderRadius: '10px',
                  my: 0.3,
                  py: 1.1,
                  px: 1.8,
                  transition: 'all 120ms ease',
                  backgroundColor: isSelected
                    ? isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(2, 132, 199, 0.08)'
                    : 'transparent',
                  border: isSelected
                    ? `1px solid ${isDark ? 'rgba(56, 189, 248, 0.35)' : 'rgba(2, 132, 199, 0.3)'}`
                    : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.category}
                  primaryTypographyProps={{
                    fontSize: '0.88rem',
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? theme.palette.text.primary : theme.palette.text.primary,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.68rem',
                    fontFamily: '"JetBrains Mono", monospace',
                    color: isSelected ? theme.palette.secondary.main : theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    mt: 0.2,
                  }}
                />
                {item.shortcut && (
                  <Chip
                    label={item.shortcut}
                    size="small"
                    sx={{
                      fontSize: '0.62rem',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 700,
                      borderRadius: '4px',
                      height: 20,
                    }}
                  />
                )}
                {isSelected && (
                  <ArrowForwardIosIcon sx={{ fontSize: 12, color: theme.palette.secondary.main, ml: 1 }} />
                )}
              </ListItemButton>
            );
          })
        )}
      </List>

      {/* Footer Navigation Hints */}
      <Box
        sx={{
          px: 2.5,
          py: 1.2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.8, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.66rem', color: theme.palette.text.secondary }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>↑↓</Box> Navigate
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.66rem', color: theme.palette.text.secondary }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>↵</Box> Select
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.66rem', color: theme.palette.text.secondary }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>ESC</Box> Close
          </Typography>
        </Box>

        {copiedToast && (
          <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', color: '#34d399', fontWeight: 700 }}>
            ✓ Email copied to clipboard!
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
